/**
 * PESSULUS's measured tables and plane bounds. PURE — no audio, no DOM.
 *
 * Lives in music/ (not audio/warble/) so the mapper can read the rung table
 * and plane bounds without violating invariant 2: mapping/ imports nothing
 * from audio/ except common/pitch. Same precedent as STROKE_ORDER in talas.
 *
 * Provenance: Chirp/warble.html lines 604-631 at 6f4d80a, values verbatim.
 *
 * Why tables and not formulas: the textbook Hopf readout γ√β/2π is 59% low at
 * β = 0.002 and 2.5% low at β = 3 — unusable as an instrument readout. These
 * are 13 knots measured off the render, interpolated log-log; f₀ scales
 * exactly with γ, so one table serves every register. The threshold curve has
 * a fold in it: 0.147 at β = 0.002, falling to 0 at β ≈ 0.17 where the Hopf
 * takes over from the saddle-node.
 */

const F0K: ReadonlyArray<readonly [number, number]> = [
  [0.002, 0.017227], [0.005, 0.022233], [0.01, 0.02877], [0.02, 0.038565], [0.05, 0.057495],
  [0.1, 0.076438], [0.2, 0.099155], [0.3, 0.114498], [0.6, 0.146298], [1, 0.177071],
  [1.6, 0.214124], [2, 0.235616], [3, 0.282662],
];

/** Fundamental frequency readout for a given gamma and beta, in Hz. */
export function F0(gamma: number, beta: number): number {
  const b = Math.max(F0K[0][0], Math.min(F0K[F0K.length - 1][0], beta));
  for (let i = 0; i < F0K.length - 1; i++) {
    const A = F0K[i], B = F0K[i + 1];
    if (b <= B[0]) {
      const t = (Math.log(b) - Math.log(A[0])) / (Math.log(B[0]) - Math.log(A[0]));
      return gamma * Math.exp(Math.log(A[1]) + t * (Math.log(B[1]) - Math.log(A[1])));
    }
  }
  return gamma * F0K[F0K.length - 1][1];
}

const THRK: ReadonlyArray<readonly [number, number]> = [
  [0.002, 0.1468], [0.004, 0.1455], [0.008, 0.1428], [0.015, 0.1382], [0.03, 0.1284],
  [0.05, 0.1155], [0.07, 0.1028], [0.09, 0.0903], [0.11, 0.0779], [0.13, 0.0642],
  [0.15, 0.0459], [0.17, 0], [3, 0],
];

/** The phonation threshold: below THR(beta), alpha produces silence. */
export function THR(beta: number): number {
  if (beta <= THRK[0][0]) return THRK[0][1];
  for (let i = 0; i < THRK.length - 1; i++) {
    const A = THRK[i], B = THRK[i + 1];
    if (beta <= B[0]) {
      const t = (Math.log(beta) - Math.log(A[0])) / (Math.log(B[0]) - Math.log(A[0]));
      return A[1] + t * (B[1] - A[1]);
    }
  }
  return 0;
}

/** The readout's honest one-liner about where (α, β) sits. */
export function regimeOf(alpha: number, beta: number): string {
  if (alpha <= THR(beta)) return 'below the phonation threshold — silence';
  if (beta < 0.02) return 'relaxation cycle — buzzy';
  if (beta < 0.15) return 'anharmonic — bright';
  if (beta < 0.6) return 'moderately anharmonic — the songbird note';
  if (beta < 1.0) return 'near-sinusoidal — a clean tone';
  return 'near-Hopf — a pure whistle';
}

/** The plane bounds (warble.html:631). */
export const BMIN = 0.002;
export const BMAX = 3;
export const AMIN = -0.15;
export const AMAX = 0.75;

/** PERCH: the pitch axis quantized to thirteen rungs (warble.html:1570). */
export const PERCH_B: readonly number[] =
  [0.02, 0.035, 0.06, 0.1, 0.16, 0.26, 0.42, 0.62, 0.9, 1.25, 1.7, 2.2, 2.9];

export const betaFromRung = (i: number): number =>
  PERCH_B[Math.max(0, Math.min(PERCH_B.length - 1, i))];

/** PLANE: continuous 0..1 → BMIN..BMAX, log-interpolated like the f₀ table. */
export function betaFromContinuous(t01: number): number {
  const t = Math.max(0, Math.min(1, t01));
  return Math.exp(Math.log(BMIN) + t * (Math.log(BMAX) - Math.log(BMIN)));
}

/** The perch label format (warble.html:1571-1578): '840' or '1.2k'. */
export const formatF0 = (hz: number): string =>
  hz >= 1000 ? `${(hz / 1000).toFixed(1)}k` : `${Math.round(hz)}`;
